import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherInfo, FeatherRefreshCw } from "@subframe/core";

import { Dialog, DialogContent } from "@/components/ui/dialog";

interface RegenerateInstancesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  isRegenerating: boolean;
  onRegenerate: () => void;
}

export function RegenerateInstancesDialog({
  open,
  onOpenChange,
  templateName,
  isRegenerating,
  onRegenerate,
}: RegenerateInstancesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-[448px]">
        <div className="flex h-full w-full flex-col items-start gap-6 px-8 py-8">
          <div className="flex w-full items-center gap-4">
            <IconWithLogoBackground
              variant="neutral"
              size="large"
              icon={<FeatherRefreshCw />}
            />
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Apply to future instances?
              </span>
              <span className="text-body font-body text-subtext-color">
                Your schedule changes have been saved.
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body font-body text-default-font">
              Would you like to apply these changes to all future instances of
              &quot;{templateName}&quot;? This will:
            </span>
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex w-full items-start gap-2">
                <span className="text-body font-body text-default-font">•</span>
                <span className="text-body font-body text-default-font">
                  Cancel all upcoming scheduled instances
                </span>
              </div>
              <div className="flex w-full items-start gap-2">
                <span className="text-body font-body text-default-font">•</span>
                <span className="text-body font-body text-default-font">
                  Regenerate future instances with the new schedule
                </span>
              </div>
              <div className="flex w-full items-start gap-2">
                <span className="text-body font-body text-default-font">•</span>
                <span className="text-body font-body text-default-font">
                  Keep all historical completion data
                </span>
              </div>
            </div>
            <div className="flex w-full items-start gap-2 rounded-sm bg-neutral-50 px-3 py-3">
              <FeatherInfo className="text-body font-body text-subtext-color" />
              <span className="grow shrink-0 basis-0 text-caption font-caption text-subtext-color">
                If you skip this step, future instances will keep the old
                schedule until you manually pause and republish.
              </span>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-secondary"
              size="large"
              onClick={() => onOpenChange(false)}
            >
              Skip for now
            </Button>
            <Button
              variant="brand-primary"
              size="large"
              onClick={onRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? "Applying..." : "Apply changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}